/**
 * Feature eligibility — decides which portal features are relevant for a user
 * based on the feature's `config.requires` (service-enablement) measured against
 * the service flags on the user's rooftops (customers).
 *
 * This complements the existing `config.tiers` gate (see FeatureTreeSelector):
 * tiers gate on the rooftop's tier, requires gate on whether a rooftop has the
 * underlying service turned on (e.g. Guest Rides, Appraisals, Mobile Service).
 *
 * UX gate only — route guards (ProtectedRoute + the user's permissions) and the
 * per-rooftop runtime checks elsewhere in the portal remain the real boundary.
 *
 * Pure functions — no React, no GQL, no side effects.
 */

import { readGuestridesAccessSection } from '@hopdrive/sdk-configs';

/** Minimal rooftop (customer) shape needed for service-enablement gating. */
export interface EligibilityRooftop {
  tier?: string | null;
  effective_config?: Record<string, any> | null;
  /**
   * The rooftop's active contracts, each with the product keys its triggers
   * own. Card #2346: Appraisals is granted by a trigger, not by a config flag,
   * and this is how the query hands that in. Absent means the caller did not
   * select it, which reads as no product-owned service.
   */
  contracts?: Array<{ artriggers?: Array<{ product_key?: string | null }> | null } | null> | null;
}

/** Whether an active contract on this rooftop owns the product key. */
function ownsProduct(rooftop: EligibilityRooftop, productKey: string): boolean {
  return (rooftop.contracts ?? []).some(contract =>
    (contract?.artriggers ?? []).some(trigger => trigger?.product_key === productKey),
  );
}

/**
 * Service-key → "is this service enabled on this rooftop?" predicate.
 *
 * Keys match the `requires` tokens in `features.config`. The answers do not come
 * from one place by design: Guest Rides and Mobile Service still read their
 * config switches, while Appraisals reads the contract trigger that owns it
 * (card #2346). None of them reads the portal products: this is evaluated over
 * every rooftop a user is assigned to (any-rooftop semantics), and the
 * portal-products snapshot covers the working rooftop only, so it cannot answer
 * for the others.
 */
export const SERVICE_ENABLEMENT: Record<string, (rooftop: EligibilityRooftop) => boolean> = {
  guestrides: r => readGuestridesAccessSection(r.effective_config ?? undefined)?.enabled === true,
  appraisals: r => ownsProduct(r, 'appraisals'),
  // `requires` token is "mobile-service" (hyphen); the rooftop config key is
  // `mobile_service` (underscore) with `.active`.
  'mobile-service': r => r.effective_config?.mobile_service?.active === true,
};

/**
 * The set of service keys enabled on at least ONE of the given rooftops
 * (ANY-rooftop semantics — a feature is relevant if any assigned rooftop has its
 * service on; per-rooftop access is still enforced at runtime).
 */
export function enabledServiceKeys(rooftops: EligibilityRooftop[]): Set<string> {
  const enabled = new Set<string>();
  for (const [key, isEnabled] of Object.entries(SERVICE_ENABLEMENT)) {
    if (rooftops.some(isEnabled)) enabled.add(key);
  }
  return enabled;
}

/**
 * Does a feature's `requires` gate pass, given the set of enabled service keys?
 * Features with no `requires` always pass. An unknown required key never passes
 * (it can't be in `enabledServiceKeys`), so the feature stays hidden rather than
 * silently shown.
 */
export function meetsServiceRequirements(
  requires: string[] | null | undefined,
  enabledServices: Set<string>,
): boolean {
  if (!requires || requires.length === 0) return true;
  return requires.every(key => enabledServices.has(key));
}

/** Eligibility inputs derived from a user's rooftops. */
export interface RooftopEligibility {
  /** Distinct tiers across the rooftops (for the `config.tiers` gate + Lite badge). */
  tiers: Set<string>;
  /** Service keys enabled on at least one rooftop (for the `config.requires` gate). */
  enabledServices: Set<string>;
}

/**
 * THE single source of truth for turning a user's rooftops into the eligibility
 * inputs (tiers + enabled services). Used by every feature-selection surface
 * (user Features tab, Add User wizard) so they gate identically.
 */
export function deriveRooftopEligibility(rooftops: EligibilityRooftop[]): RooftopEligibility {
  const tiers = new Set<string>();
  for (const r of rooftops) {
    // Cross-rooftop `customers.tier` read (named exception): the product-access snapshot covers only the working rooftop, and this decision spans every rooftop the user is linked to.
    if (r.tier) tiers.add(r.tier);
  }
  return { tiers, enabledServices: enabledServiceKeys(rooftops) };
}

/**
 * THE single source of truth for deciding which features to show. Applies both
 * gates against the derived eligibility:
 *  - tiers:    feature's `config.tiers` must overlap the rooftops' tiers (empty = all)
 *  - requires: feature's `config.requires` services must be enabled on a rooftop
 *
 * Each gate is only applied when its input is provided, so callers that don't
 * pass `enabledServices` (e.g. surfaces with no service gating) are unaffected.
 * Generic over the feature shape — works for both the hand-written `Feature`
 * type and the generated `Features` type (config is jsonb).
 */
export function filterEligibleFeatures<T>(
  features: T[],
  { tiers, enabledServices }: Partial<RooftopEligibility>,
): T[] {
  return features.filter(f => {
    const cfg = (f as any)?.config as FeatureConfigGates | null | undefined;

    // Evaluate each declared gate to pass(true)/fail(false), or undefined when the gate
    // is absent or can't be evaluated in this context.
    let tierPass: boolean | undefined;
    if (tiers && tiers.size > 0 && cfg?.tiers?.length) {
      tierPass = cfg.tiers.some(t => tiers.has(t));
    }
    let servicePass: boolean | undefined;
    if (enabledServices && cfg?.requires?.length) {
      servicePass = meetsServiceRequirements(cfg.requires, enabledServices);
    }

    // 'any' → either declared gate passing is enough (tier OR service).
    if (cfg?.match === 'any') {
      const results = [tierPass, servicePass].filter(r => r !== undefined) as boolean[];
      return results.length === 0 || results.some(Boolean);
    }

    // default 'all' → every declared, evaluable gate must pass (tier AND service).
    if (tierPass === false) return false;
    if (servicePass === false) return false;
    return true;
  });
}

interface FeatureConfigGates {
  tiers?: string[] | null;
  requires?: string[] | null;
  match?: 'all' | 'any';
}
